import { Button } from '@civilgrid/ui/components';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type Control, type UseFormReturn, useWatch } from 'react-hook-form';
import { outcomeLabels, requestStatusLabels, utilitiesPresentLabels } from '~/common/dataRequestUtils';
import { type DataRequestV2, Outcome, RequestStatus, UtilityPresence } from '~/common/types';
import Separator from '~/components2/ContextMenu/Separator';
import ControlledDatePicker from '~/components2/forms/controlled/ControlledDatePicker';
import ControlledSelect from '~/components2/forms/controlled/ControlledSelect';
import Input from '~/components2/forms/Input';
import { addBusinessDays, calcBusinessDaysDifference } from '~/helpers/calcBusinessDays';
import type { TFormValues } from './GeneralForm';

interface Props {
  control: Control<TFormValues>;
  form: UseFormReturn<TFormValues>;
  dataRequest: DataRequestV2;
  initialRequestStatus: RequestStatus;
}

function getContactedAtFieldName(status: RequestStatus): keyof TFormValues | null {
  const statusToDateFieldMap: Partial<Record<RequestStatus, keyof TFormValues>> = {
    [RequestStatus.Pending]: 'createdAt',
    [RequestStatus.InProgress0]: 'firstContactedAt',
    [RequestStatus.InProgress1]: 'secondContactedAt',
    [RequestStatus.InProgress2]: 'thirdContactedAt',
    [RequestStatus.InProgress3]: 'fourthContactedAt',
  };
  return statusToDateFieldMap[status] || null;
}

function getContactedAtLabel(fieldName: keyof TFormValues | null): string {
  if (!fieldName) return 'Contacted At';
  const labelMap: Record<string, string> = {
    createdAt: 'Request Created At',
    firstContactedAt: 'First Contacted At',
    secondContactedAt: 'Second Contacted At',
    thirdContactedAt: 'Third Contacted At',
    fourthContactedAt: 'Fourth Contacted At',
  };
  return labelMap[fieldName] || 'Contacted At';
}

function getNextFollowUpStatus(status: RequestStatus): RequestStatus | null {
  const statusMap: Partial<Record<RequestStatus, RequestStatus>> = {
    [RequestStatus.Pending]: RequestStatus.InProgress0,
    [RequestStatus.InProgress0]: RequestStatus.InProgress1,
    [RequestStatus.InProgress1]: RequestStatus.InProgress2,
    [RequestStatus.InProgress2]: RequestStatus.InProgress3,
  };
  return statusMap[status] || null;
}

function isQueuedOrInProgressStatus(status: RequestStatus): boolean {
  return (
    status === RequestStatus.Pending ||
    status === RequestStatus.InProgress0 ||
    status === RequestStatus.InProgress1 ||
    status === RequestStatus.InProgress2 ||
    status === RequestStatus.InProgress3
  );
}

function isCompleteStatus(status: RequestStatus): boolean {
  return status === RequestStatus.Complete || status === RequestStatus.Cancelled || status === RequestStatus.Dismissed;
}

/**
 * Gets the default duration for the next follow-up stage
 *
 * Note:
 * - When moving Pending (InQueue) → InProgress0: set firstContactedAt to today, nextFollowUpAt = firstContactedAt + secondFollowUpDuration
 * - When moving InProgress0 → InProgress1: set secondContactedAt to today, nextFollowUpAt = secondContactedAt + thirdFollowUpDuration
 */
function getDefaultDuration(nextStatus: RequestStatus, agency: DataRequestV2['agency']): number {
  const durationMap: Partial<Record<RequestStatus, number>> = {
    [RequestStatus.InProgress0]: agency.secondFollowUpDuration, // Moving to InProgress0 uses secondFollowUpDuration
    [RequestStatus.InProgress1]: agency.thirdFollowUpDuration, // Moving to InProgress1 uses thirdFollowUpDuration
    [RequestStatus.InProgress2]: agency.fourthFollowUpDuration,
    [RequestStatus.InProgress3]: agency.timeoutDuration,
  };
  return durationMap[nextStatus] ?? agency.firstFollowUpDuration;
}

export default function FollowUpPanel({ control, form, dataRequest, initialRequestStatus }: Props) {
  const requestStatus = useWatch({ control, name: 'requestStatus' });
  const nextFollowUpAt = useWatch({ control, name: 'nextFollowUpAt' });
  const formValues = useWatch({ control });

  const [helperButtonClicked, setHelperButtonClicked] = useState(false);
  // Local state for daysAfter input while user is typing (before commit)
  const [daysAfterInput, setDaysAfterInput] = useState<string>('');
  const [usedCompleteUnresponsiveRequestHelper, setUsedCompleteUnresponsiveRequestHelper] = useState(false);

  const requestStatusItems = useMemo(() => {
    return Object.values(RequestStatus).map((value) => ({
      value,
      label: <div>{requestStatusLabels[value]}</div>,
      keywords: [requestStatusLabels[value]],
    }));
  }, []);

  const utilityPresenceItems = useMemo(() => {
    return Object.values(UtilityPresence).map((value) => ({
      value,
      label: <div className="text-xs">{utilitiesPresentLabels[value]}</div>,
      keywords: [utilitiesPresentLabels[value]],
    }));
  }, []);

  const outcomeItems = useMemo(() => {
    return Object.values(Outcome).map((value) => ({
      value,
      label: <div className="text-xs">{outcomeLabels[value]}</div>,
      keywords: [outcomeLabels[value]],
    }));
  }, []);

  const contactedAtFieldName = useMemo(() => getContactedAtFieldName(requestStatus), [requestStatus]);

  const contactedAtLabel = useMemo(() => getContactedAtLabel(contactedAtFieldName), [contactedAtFieldName]);

  const initialNextFollowUpStatus = useMemo(() => getNextFollowUpStatus(initialRequestStatus), [initialRequestStatus]);

  // Check if we can use the helper - based on initial state and whether we've clicked
  const canUseMoveToNextFollowUpStage = useMemo(() => {
    return initialNextFollowUpStatus !== null && !helperButtonClicked && requestStatus === initialRequestStatus;
  }, [initialNextFollowUpStatus, helperButtonClicked, requestStatus, initialRequestStatus]);

  const canUseCompleteRequest = useMemo(() => {
    return !isCompleteStatus(requestStatus);
  }, [requestStatus]);

  // Get the current contactedAt value
  const contactedAtValue = useMemo(() => {
    if (!contactedAtFieldName) return null;
    return formValues[contactedAtFieldName] as DateTime | undefined;
  }, [contactedAtFieldName, formValues]);

  // Calculate daysAfter display value from nextFollowUpAt (single source of truth)
  const calculatedDaysAfter = useMemo(() => {
    if (!nextFollowUpAt || !contactedAtValue) {
      return '';
    }
    const businessDays = calcBusinessDaysDifference(contactedAtValue, nextFollowUpAt as DateTime);
    return businessDays.toString();
  }, [nextFollowUpAt, contactedAtValue]);

  // Sync daysAfterInput with calculatedDaysAfter when nextFollowUpAt changes externally
  // (e.g., from date picker or helper button)
  // Only update if the calculated value differs from current input to avoid overwriting user input
  useEffect(() => {
    if (calculatedDaysAfter !== daysAfterInput) {
      setDaysAfterInput(calculatedDaysAfter);
    }
  }, [calculatedDaysAfter, daysAfterInput]);

  // Handle days after input change - update nextFollowUpAt immediately
  const handleDaysAfterChange = useCallback(
    (value: string) => {
      setDaysAfterInput(value);

      if (!contactedAtValue) {
        if (isQueuedOrInProgressStatus(requestStatus)) {
          form.setValue('nextFollowUpAt', DateTime.now(), {
            shouldDirty: true,
          });
        }
        return;
      }

      if (value === '') {
        if (isQueuedOrInProgressStatus(requestStatus)) {
          form.setValue('nextFollowUpAt', contactedAtValue, {
            shouldDirty: true,
          });
        }
        return;
      }

      const days = parseInt(value, 10);
      if (!Number.isNaN(days) && days >= 0) {
        const newNextFollowUpAt = addBusinessDays(contactedAtValue, days);
        form.setValue('nextFollowUpAt', newNextFollowUpAt, {
          shouldDirty: true,
        });
      }
    },
    [contactedAtValue, form, requestStatus],
  );

  const handleMoveToNextFollowUpStage = useCallback(() => {
    if (!initialNextFollowUpStatus) return;

    // Update requestStatus to the next follow-up stage
    // This will trigger the useEffect in GeneralForm to auto-set contactedAt
    form.setValue('requestStatus', initialNextFollowUpStatus, {
      shouldDirty: true,
    });

    // Calculate nextFollowUpAt
    // e.g., InProgress0→InProgress1: nextFollowUpAt = secondContactedAt (now) + thirdFollowUpDuration
    const defaultDuration = getDefaultDuration(initialNextFollowUpStatus, dataRequest.agency);
    const nextFollowUpAt = addBusinessDays(DateTime.now(), defaultDuration);
    form.setValue('nextFollowUpAt', nextFollowUpAt, { shouldDirty: true });
    setHelperButtonClicked(true);
  }, [initialNextFollowUpStatus, form, dataRequest.agency]);

  const handleCompleteRequest = useCallback(() => {
    form.setValue('requestStatus', RequestStatus.Complete, {
      shouldDirty: true,
    });
    form.setValue('outcome', undefined, {
      shouldDirty: true,
    });
    form.setValue('utilityPresence', undefined, {
      shouldDirty: true,
    });
  }, [form]);

  const handleCompleteUnresponsiveRequest = useCallback(() => {
    setUsedCompleteUnresponsiveRequestHelper(true);
    form.setValue('requestStatus', RequestStatus.Complete, {
      shouldDirty: true,
    });
    form.setValue('outcome', Outcome.AgencyUnresponsive, {
      shouldDirty: true,
    });
    form.setValue('utilityPresence', UtilityPresence.PresenceUnknown, {
      shouldDirty: true,
    });
  }, [form]);

  // Check if we should show the Called Date field (only for thirdContactedAt)
  const showCalledDate = contactedAtFieldName === 'thirdContactedAt';

  const showNextFollowUpAt = useMemo(() => {
    return !isCompleteStatus(requestStatus);
  }, [requestStatus]);

  const showDaysAfter = useMemo(() => {
    return isQueuedOrInProgressStatus(requestStatus);
  }, [requestStatus]);

  const showOutcome = useMemo(() => {
    return requestStatus === RequestStatus.Complete;
  }, [requestStatus]);

  const showCompleteUnresponsiveRequest = useMemo(() => {
    return (
      requestStatus === RequestStatus.InProgress3 &&
      nextFollowUpAt &&
      nextFollowUpAt.startOf('day') <= DateTime.now().startOf('day')
    );
  }, [requestStatus, nextFollowUpAt]);

  return (
    <div className="mt-4 rounded border p-4 bg-grey-10 space-y-4">
      <div className="text-xs font-medium">Follow-up Management</div>
      <div
        className={classNames(
          'grid gap-4',
          showCalledDate ? 'grid-cols-3 lg:grid-cols-[1fr_1fr_0.75fr_1fr_0.75fr]' : 'grid-cols-2 lg:grid-cols-4',
        )}
      >
        <ControlledSelect
          name="requestStatus"
          label="Request Status"
          control={control}
          items={requestStatusItems}
          isRequired
          popoverTriggerClassName="bg-white w-full"
        />
        {contactedAtFieldName && contactedAtLabel && (
          <ControlledDatePicker
            control={control}
            label={contactedAtLabel}
            name={contactedAtFieldName}
            disabled={contactedAtFieldName === 'createdAt'}
            inputClassName="bg-white!"
          />
        )}
        {showCalledDate && (
          <ControlledDatePicker control={control} label="Called Date" name="calledDate" inputClassName="bg-white" />
        )}
        {showNextFollowUpAt && (
          <ControlledDatePicker
            control={control}
            label={requestStatus === RequestStatus.InProgress3 ? 'Timed out at' : 'Next follow up at'}
            name="nextFollowUpAt"
            inputClassName="bg-white"
            minDate={DateTime.now()}
            required={isQueuedOrInProgressStatus(requestStatus)}
            validate={(value, formValues) => {
              if (isQueuedOrInProgressStatus(formValues.requestStatus)) {
                return isEmpty(value) ? 'Cannot be empty' : true;
              } else {
                return true;
              }
            }}
          />
        )}
        {showDaysAfter && (
          <Input
            label="Days After"
            type="number"
            value={daysAfterInput}
            onChange={handleDaysAfterChange}
            placeholder="Enter days"
            className="bg-white"
            tooltipText="The number of business days between the contacted at date and the next follow up at date"
          />
        )}
        {showOutcome && (
          <ControlledDatePicker
            control={form.control}
            label="Resolution Date"
            name="resolutionDate"
            inputClassName="bg-white"
          />
        )}
        {showOutcome && (
          <ControlledSelect
            name="utilityPresence"
            label="Utility Presence"
            control={form.control}
            items={utilityPresenceItems}
            isClearable={true}
            isRequired={requestStatus === RequestStatus.Complete}
            validate={(value, formValues) => {
              if (formValues.requestStatus === RequestStatus.Complete) {
                return isEmpty(value) ? 'Cannot be empty' : true;
              } else {
                return true;
              }
            }}
            popoverTriggerClassName="w-full bg-white"
          />
        )}
        {showOutcome && (
          <ControlledSelect
            name="outcome"
            label="Outcome"
            control={form.control}
            items={outcomeItems}
            isClearable={true}
            isRequired={requestStatus === RequestStatus.Complete}
            validate={(_, formValues) => {
              if (formValues.requestStatus === RequestStatus.Complete) {
                if (formValues.utilityPresence && formValues.utilityPresence !== UtilityPresence.OutsideServiceArea) {
                  const validValuesMap = {
                    [UtilityPresence.Present]: [Outcome.AgencyNotSharing, Outcome.DocumentsReceived],
                    [UtilityPresence.NoAssetsPresent]: [Outcome.AgencyResponded, Outcome.ReferredToOtherAgency],
                    [UtilityPresence.PresenceUnknown]: [
                      Outcome.AgencyUnresponsive,
                      Outcome.AgencyNotSharing,
                      Outcome.ReferredToOtherAgency,
                    ],
                  };
                  const validValues = validValuesMap[formValues.utilityPresence];
                  const validOptions = `Valid options - ${validValues.map((v) => outcomeLabels[v]).join(', ')}`;
                  if (formValues.outcome) {
                    if (!validValues.includes(formValues.outcome)) {
                      return validOptions;
                    } else {
                      return true; // valid
                    }
                  } else {
                    return validOptions;
                  }
                } else {
                  return true;
                }
              } else {
                return true;
              }
            }}
            popoverTriggerClassName="w-full bg-white"
          />
        )}
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="secondary"
          disabled={!canUseMoveToNextFollowUpStage}
          onClick={handleMoveToNextFollowUpStage}
          className="bg-white! hover:bg-grey-20!"
        >
          Move to next follow-up stage
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={!canUseCompleteRequest}
          onClick={handleCompleteRequest}
          className="bg-white! hover:bg-grey-20!"
        >
          Complete Request
        </Button>
        {(showCompleteUnresponsiveRequest || usedCompleteUnresponsiveRequestHelper) && (
          <Button
            type="button"
            variant="secondary"
            disabled={usedCompleteUnresponsiveRequestHelper}
            onClick={handleCompleteUnresponsiveRequest}
          >
            Complete Unresponsive Request
          </Button>
        )}
      </div>
      <div className="h-[10px] flex items-end w-full">
        <Separator className="h-[1px] bg-gray-300 w-full" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <ControlledDatePicker control={form.control} label="First Contacted At" name="firstContactedAt" />
        <ControlledDatePicker control={form.control} label="Second Contacted At" name="secondContactedAt" />
        <ControlledDatePicker
          control={form.control}
          label="Third Contacted At (Give them a call)"
          name="thirdContactedAt"
        />
        <ControlledDatePicker control={form.control} label="Called Date" name="calledDate" />
        <ControlledDatePicker control={form.control} label="Fourth Contacted At" name="fourthContactedAt" />
        <ControlledDatePicker control={form.control} label="Resolution Date" name="resolutionDate" />
      </div>
    </div>
  );
}
